/**
 * Node renderer.
 *
 * One function per node type, no escape hatch. A node that resolves to an empty
 * value renders `null`, and a container left with nothing but spacing collapses,
 * so optional event fields need no `when` guard — see MASTER_TEMPLATE §6.2 and
 * `renderContent` below.
 */

import { Fragment, type CSSProperties, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { evaluateWhen } from "./expression";
import { resolveCollection, resolvePath, resolveValue } from "./context";
import { ICONS } from "./icons";
import type { TokenResolver } from "./tokens";
import type {
  BoxNode, ButtonNode, DividerNode, DotsNode, EngineContext, GridNode, IconNode,
  ImageNode, LinkNode, RepeatNode, RepeatScope, SlotNode, SpacerNode, StackNode,
  SvgNode, TemplateNode, TextNode, UseNode,
} from "./types";

export type RenderEnv = {
  ctx: EngineContext;
  tokens: TokenResolver;
  predicates: Record<string, boolean>;
  /** Host-owned widgets, mounted where the template places a `slot`. */
  slots: Partial<Record<SlotNode["name"], ReactNode>>;
  /** Named fragments from layout.json, referenced by `use` nodes. */
  fragments: Record<string, TemplateNode>;
  /** Slots the template declared, collected during render and mounted at the root. */
  declaredSlots: Set<SlotNode["name"]>;
};

/** Guards against a fragment that references itself, directly or in a cycle. */
const MAX_FRAGMENT_DEPTH = 8;

const ALIGN: Record<string, string> = {
  start: "flex-start", center: "center", end: "flex-end", stretch: "stretch", baseline: "baseline",
};
const JUSTIFY: Record<string, string> = {
  start: "flex-start", center: "center", end: "flex-end", between: "space-between", around: "space-around",
};

/* ------------------------------------------------------------------ nodes */

function renderStack(node: StackNode, env: RenderEnv, scope: RepeatScope, key: string) {
  const { nodes: children, empty } = renderContent(node.children, env, scope);
  if (empty) return null;

  const base = node.dir === "row" ? "row" : "column";
  const style: CSSProperties & Record<string, string | undefined> = {
    display: "flex",
    gap: env.tokens.space(node.gap),
    alignItems: node.align ? ALIGN[node.align] : node.dir === "row" ? "center" : undefined,
    justifyContent: node.justify ? JUSTIFY[node.justify] : undefined,
    flexWrap: node.wrap ? "wrap" : undefined,
    maxWidth: env.tokens.maxWidth(node.maxWidth),
    padding: env.tokens.padding(node.padding),
    textAlign: node.align_text,
    width: node.maxWidth ? "100%" : undefined,
    marginLeft: node.maxWidth ? "auto" : undefined,
    marginRight: node.maxWidth ? "auto" : undefined,
    // Direction is responsive, so it rides on custom properties the token
    // stylesheet reads at each breakpoint rather than on an inline value.
    "--dir-base": base,
    "--dir-sm": node.dirSm && (node.dirSm === "row" ? "row" : "column"),
    "--dir-md": node.dirMd && (node.dirMd === "row" ? "row" : "column"),
    "--dir-lg": node.dirLg && (node.dirLg === "row" ? "row" : "column"),
  };

  return <div key={key} className="jtpk-dir" style={style as CSSProperties}>{children}</div>;
}

function renderGrid(node: GridNode, env: RenderEnv, scope: RepeatScope, key: string) {
  const { nodes: children, empty } = renderContent(node.children, env, scope);
  if (empty) return null;

  const style: CSSProperties & Record<string, string | undefined> = {
    display: "grid",
    gap: env.tokens.space(node.gap),
    maxWidth: env.tokens.maxWidth(node.maxWidth),
    width: node.maxWidth ? "100%" : undefined,
    marginLeft: node.maxWidth ? "auto" : undefined,
    marginRight: node.maxWidth ? "auto" : undefined,
    "--cols-base": String(node.cols.base),
    "--cols-sm": node.cols.sm === undefined ? undefined : String(node.cols.sm),
    "--cols-md": node.cols.md === undefined ? undefined : String(node.cols.md),
    "--cols-lg": node.cols.lg === undefined ? undefined : String(node.cols.lg),
  };

  return <div key={key} className="jtpk-grid" style={style as CSSProperties}>{children}</div>;
}

function boxStyle(node: BoxNode, env: RenderEnv): CSSProperties {
  // Column span is responsive like the column count: a `span md:6` must not apply
  // on a one-column phone grid, where it would spawn implicit columns.
  const span: Record<string, string | undefined> = node.span
    ? {
        "--span-base": node.span.base === undefined ? undefined : String(node.span.base),
        "--span-sm": node.span.sm === undefined ? undefined : String(node.span.sm),
        "--span-md": node.span.md === undefined ? undefined : String(node.span.md),
        "--span-lg": node.span.lg === undefined ? undefined : String(node.span.lg),
      }
    : {};

  return {
    ...span,
    background: env.tokens.background(node.background),
    border: env.tokens.border(node.border),
    borderRadius: env.tokens.radius(node.radius),
    boxShadow: env.tokens.shadow(node.shadow),
    padding: env.tokens.padding(node.padding),
    maxWidth: env.tokens.maxWidth(node.maxWidth),
    aspectRatio: node.aspect,
    overflow: node.overflow,
    textAlign: node.align_text,
    width: node.maxWidth ? "100%" : undefined,
    marginLeft: node.maxWidth ? "auto" : undefined,
    marginRight: node.maxWidth ? "auto" : undefined,
    position: "relative",
  } as CSSProperties;
}

function renderBox(node: BoxNode, env: RenderEnv, scope: RepeatScope, key: string) {
  const { nodes: children, empty } = renderContent(node.children, env, scope);
  // A pressable box keeps rendering: its action is content a guest can reach.
  if (empty && !node.action) return null;
  const style = boxStyle(node, env);
  const className = node.span ? "jtpk-span" : undefined;

  if (node.action) {
    const label = node.actionLabel ? resolveValue(node.actionLabel, env.ctx, scope) : undefined;

    // In-page navigation is a link, not a button, so it keeps middle-click,
    // copy-link and the browser's own history behaviour.
    if (node.action === "scrollTo" && node.target) {
      return (
        <a
          key={key}
          href={`#${node.target}`}
          aria-label={label}
          className={className}
          style={{ ...style, cursor: "pointer" }}
          onClick={() => env.ctx.actions.scrollTo(node.target!)}
        >
          {children}
        </a>
      );
    }

    return (
      <button
        key={key}
        type="button"
        aria-label={label}
        className={className}
        // `display: block` so the element lays out like the div it replaces; the
        // width follows the same rule as the non-pressable branch. Forcing 100%
        // here made a row of filter tabs stack vertically.
        style={{ ...style, display: "block", cursor: "pointer" }}
        onClick={() => invokeAction(node.action!, node.target, env, scope)}
      >
        {children}
      </button>
    );
  }

  return <div key={key} className={className} style={style}>{children}</div>;
}

function renderText(node: TextNode, env: RenderEnv, scope: RepeatScope, key: string) {
  const text = resolveValue(node.value, env.ctx, scope);
  if (text === "") return null;

  const Tag = (node.as ?? "p") as keyof JSX.IntrinsicElements;
  const style: CSSProperties = {
    ...env.tokens.textStyle(node.style),
    color: env.tokens.color(node.color),
    textAlign: node.align,
    maxWidth: env.tokens.maxWidth(node.maxWidth),
    marginLeft: node.maxWidth ? "auto" : undefined,
    marginRight: node.maxWidth ? "auto" : undefined,
    whiteSpace: "pre-line",
  };

  return <Tag key={key} style={style}>{text}</Tag>;
}

function renderImage(node: ImageNode, env: RenderEnv, scope: RepeatScope, key: string) {
  const src = resolveValue(node.src, env.ctx, scope);
  if (src === "") return null;
  const alt = resolveValue(node.alt, env.ctx, scope);

  return (
    <div
      key={key}
      style={{
        position: "relative",
        overflow: "hidden",
        aspectRatio: node.aspect,
        borderRadius: env.tokens.radius(node.radius),
        width: "100%",
        height: node.aspect ? undefined : "100%",
      }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={cn(node.hover === "zoom" && "transition-transform duration-700 hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100")}
        style={{ width: "100%", height: "100%", objectFit: node.fit ?? "cover" }}
      />
      {node.overlay && (
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: env.tokens.background(node.overlay) }} />
      )}
    </div>
  );
}

function renderIcon(node: IconNode, env: RenderEnv, key: string) {
  const Icon = ICONS[node.name];
  if (!Icon) return null;
  const size = node.size ?? 16;
  const color = env.tokens.color(node.color);
  return (
    <Icon
      key={key}
      aria-hidden="true"
      width={size}
      height={size}
      style={{
        color,
        opacity: node.opacity,
        transform: node.rotate ? `rotate(${node.rotate}deg)` : undefined,
        fill: node.fill ? color : undefined,
        flexShrink: 0,
      }}
    />
  );
}

const SVG_POSITIONS: Record<string, CSSProperties> = {
  "top-left": { position: "absolute", top: 0, left: 0 },
  "top-right": { position: "absolute", top: 0, right: 0 },
  "bottom-left": { position: "absolute", bottom: 0, left: 0 },
  "bottom-right": { position: "absolute", bottom: 0, right: 0 },
  cover: { position: "absolute", inset: 0 },
};

function renderSvg(node: SvgNode, env: RenderEnv, key: string) {
  const flips = [node.flipX && "scaleX(-1)", node.flipY && "scaleY(-1)"].filter(Boolean).join(" ");

  return (
    <div
      key={key}
      aria-hidden="true"
      style={{
        ...(node.position && node.position !== "static" ? SVG_POSITIONS[node.position] : {}),
        width: node.width,
        height: node.height,
        transform: flips || undefined,
        pointerEvents: "none",
      }}
    >
      <svg viewBox={node.viewBox} fill="none" style={{ width: "100%", height: "100%" }}>
        {node.shapes.map((shape, index) => {
          const { k, fill, stroke, ...rest } = shape as Record<string, unknown> & { k: string };
          const props: Record<string, unknown> = {
            ...rest,
            fill: typeof fill === "string" ? env.tokens.color(fill) ?? fill : undefined,
            stroke: typeof stroke === "string" ? env.tokens.color(stroke) ?? stroke : undefined,
          };
          const Shape = k as keyof JSX.IntrinsicElements;
          return <Shape key={index} {...(props as object)} />;
        })}
      </svg>
    </div>
  );
}

function renderDots(node: DotsNode, env: RenderEnv, key: string) {
  const color = env.tokens.color(node.color);
  const motion = env.tokens.motion(node.motion);
  const animate = motion && motion.primitive !== "none" && !env.ctx.prefersReducedMotion;

  return (
    <div
      key={key}
      aria-hidden="true"
      style={{
        position: node.fixed ? "fixed" : "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {node.items.map((dot, index) => (
        <div
          key={index}
          style={{
            position: "absolute",
            left: `${dot.x}%`,
            top: `${dot.y}%`,
            width: `${dot.size}px`,
            height: `${dot.size}px`,
            opacity: dot.opacity,
            borderRadius: "9999px",
            backgroundColor: color,
            filter: node.blur ? `blur(${node.blur}px)` : undefined,
            animation: animate
              ? `jtpk-${node.motion} ${dot.duration ?? motion.duration ?? 5}s ${motion.easing ?? "ease-in-out"} ${dot.delay ?? 0}s infinite alternate`
              : undefined,
          }}
        />
      ))}
    </div>
  );
}

function renderDivider(node: DividerNode, env: RenderEnv, key: string) {
  const token = env.tokens.divider(node.variant);
  if (!token) return null;
  const color = env.tokens.color(token.color) ?? token.color;
  const accent = env.tokens.color(token.accentColor) ?? token.accentColor;
  const width = node.width ?? token.width ?? 60;

  if (token.kind === "hairline") {
    return (
      <div
        key={key}
        aria-hidden="true"
        style={{
          height: 1,
          width: `${width / 16}rem`,
          margin: "0 auto",
          opacity: token.opacity ?? 0.3,
          background: `linear-gradient(to right, transparent, ${color}, transparent)`,
        }}
      />
    );
  }

  const path = `M0,1 Q${width / 4},0 ${width / 2},1 T${width},1`;
  const stroke = (
    <svg width={width} height="2" style={{ color }}>
      <path d={path} stroke="currentColor" strokeWidth={token.thickness ?? 1} fill="none" opacity={token.opacity ?? 0.4} />
    </svg>
  );

  return (
    <div key={key} aria-hidden="true" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem" }}>
      {stroke}
      <div style={{ width: 6, height: 6, borderRadius: 9999, backgroundColor: accent }} />
      {stroke}
    </div>
  );
}

function buttonStyle(styleName: string | undefined, env: RenderEnv): CSSProperties {
  const token = env.tokens.button(styleName);
  if (!token) return {};
  return {
    ...env.tokens.textStyle(token.textStyle),
    background: env.tokens.background(token.background),
    color: env.tokens.color(token.color),
    border: env.tokens.border(token.border),
    borderRadius: env.tokens.radius(token.radius),
    paddingLeft: styleName ? `var(--btnx-${styleName})` : undefined,
    paddingRight: styleName ? `var(--btnx-${styleName})` : undefined,
    paddingTop: styleName ? `var(--btny-${styleName})` : undefined,
    paddingBottom: styleName ? `var(--btny-${styleName})` : undefined,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    cursor: "pointer",
  };
}

function invokeAction(action: string, target: string | undefined, env: RenderEnv, scope: RepeatScope) {
  const { actions } = env.ctx;
  switch (action) {
    case "rsvp.open": return actions.rsvpOpen();
    case "gift.contribute": return actions.giftContribute(scope.$gift as never);
    case "lightbox.open": return actions.lightboxOpen(scope.$photoIndex as number);
    case "filter.set": return actions.filterSet(String(scope["category.id"]));
    case "share": return void actions.share();
    case "calendar.google": return actions.calendarGoogle();
    case "calendar.ics": return actions.calendarIcs();
    case "map.open": return actions.mapOpen(String(scope["ceremony.mapsUrl"] ?? ""));
    case "scrollTo": return actions.scrollTo(target ?? "");
    default:
      throw new Error(`Unknown action: ${action}`);
  }
}

function renderButton(node: ButtonNode, env: RenderEnv, scope: RepeatScope, key: string) {
  const label = resolveValue(node.label, env.ctx, scope);
  if (label === "") return null;
  const Icon = node.icon ? ICONS[node.icon] : undefined;

  return (
    <button
      key={key}
      type="button"
      data-action={node.action}
      style={{ ...buttonStyle(node.style, env), width: node.fullWidth ? "100%" : undefined }}
      onClick={() => invokeAction(node.action, node.target, env, scope)}
    >
      {Icon && node.iconPosition !== "end" && <Icon aria-hidden="true" width={16} height={16} />}
      {label}
      {Icon && node.iconPosition === "end" && <Icon aria-hidden="true" width={16} height={16} />}
    </button>
  );
}

function renderLink(node: LinkNode, env: RenderEnv, scope: RepeatScope, key: string) {
  const href = resolveValue(node.href, env.ctx, scope);
  if (href === "") return null;
  const label = resolveValue(node.label, env.ctx, scope);
  if (label === "") return null;

  return (
    <a
      key={key}
      href={href}
      target={node.external === false ? undefined : "_blank"}
      rel={node.external === false ? undefined : "noopener noreferrer"}
      style={buttonStyle(node.style, env)}
    >
      {label}
    </a>
  );
}

function renderRepeat(node: RepeatNode, env: RenderEnv, scope: RepeatScope, key: string) {
  const items = resolveCollection(node.over, env.ctx);
  if (items.length === 0) {
    return node.empty ? renderNode(node.empty, env, scope, `${key}-empty`) : null;
  }

  // Each item is one group of siblings under the same rule as a container: an
  // item whose children all resolved away is dropped, and a repeat with no item
  // left renders nothing, so it cannot hold its parent open.
  const rendered = items.map((item) => renderContent(node.children, env, { ...scope, ...item }));
  if (rendered.every((item) => item.empty)) return null;

  return (
    <Fragment key={key}>
      {rendered.map((item, index) => (item.empty ? null : <Fragment key={index}>{item.nodes}</Fragment>))}
    </Fragment>
  );
}

function renderSlot(node: SlotNode, env: RenderEnv) {
  // Declared here, mounted at the template root — see `collectSlots`. Every host
  // slot is a fixed-position overlay, and an overlay rendered inside a section is
  // trapped by that section's stacking context: the RSVP dialog ended up beneath
  // a floating navbar that was merely a sibling with a higher z-index.
  // Nothing renders in place, so a slot never holds its container open.
  env.declaredSlots.add(node.name);
  return null;
}

function renderUse(node: UseNode, env: RenderEnv, scope: RepeatScope, key: string, depth: number) {
  if (depth >= MAX_FRAGMENT_DEPTH) {
    throw new Error(`Fragment nesting exceeded ${MAX_FRAGMENT_DEPTH} levels at '${node.ref}' — check for a cycle`);
  }
  const fragment = env.fragments[node.ref];
  if (!fragment) {
    throw new Error(`Unknown fragment: ${node.ref}`);
  }
  return renderNode(fragment, env, scope, key, depth + 1);
}

function renderSpacer(node: SpacerNode, env: RenderEnv, key: string) {
  return <div key={key} aria-hidden="true" style={{ height: env.tokens.space(node.size) ?? "1rem" }} />;
}

/* ------------------------------------------------------------- dispatcher */

export function renderNode(
  node: TemplateNode,
  env: RenderEnv,
  scope: RepeatScope,
  key: string,
  depth = 0,
): ReactNode {
  if (!evaluateWhen(node.when, env.predicates, scope)) return null;

  switch (node.t) {
    case "use": return renderUse(node, env, scope, key, depth);
    case "stack": return renderStack(node, env, scope, key);
    case "grid": return renderGrid(node, env, scope, key);
    case "box": return renderBox(node, env, scope, key);
    case "text": return renderText(node, env, scope, key);
    case "image": return renderImage(node, env, scope, key);
    case "icon": return renderIcon(node, env, key);
    case "svg": return renderSvg(node, env, key);
    case "dots": return renderDots(node, env, key);
    case "divider": return renderDivider(node, env, key);
    case "button": return renderButton(node, env, scope, key);
    case "link": return renderLink(node, env, scope, key);
    case "repeat": return renderRepeat(node, env, scope, key);
    case "slot": return renderSlot(node, env);
    case "spacer": return renderSpacer(node, env, key);
    default: {
      const unknown = node as { t: string };
      throw new Error(`Unknown node type: ${unknown.t}`);
    }
  }
}

/* ------------------------------------------------------------- emptiness */

/**
 * Node types that space, separate or accompany content but are not content
 * themselves: a stack of [divider, empty text] or [icon, empty text] collapses
 * rather than drawing a lone divider or a lone icon (JUB-216, JUB-218).
 */
const SEPARATOR_TYPES: ReadonlySet<TemplateNode["t"]> = new Set(["spacer", "divider", "icon"]);

/** Follows `use` refs, so a fragment that is a divider counts as one. */
function isSeparator(node: TemplateNode, env: RenderEnv): boolean {
  let current: TemplateNode | undefined = node;
  for (let depth = 0; current?.t === "use" && depth < MAX_FRAGMENT_DEPTH; depth += 1) {
    current = env.fragments[current.ref];
  }
  return current !== undefined && SEPARATOR_TYPES.has(current.t);
}

export type RenderedContent = {
  /** Every child that rendered, separators included, in declaration order. */
  nodes: ReactNode[];
  /**
   * True when children were declared and none of them rendered content: each
   * resolved to nothing, or only spacers and dividers are left.
   */
  empty: boolean;
};

/**
 * Render a list of sibling nodes under the empty-value rule of MASTER_TEMPLATE
 * §6.2. This is the one place that decides whether a group of siblings is empty;
 * stacks, grids, boxes, repeat items and sections all ask it. Each child is
 * rendered — and so each value resolved — exactly once.
 */
export function renderContent(nodes: TemplateNode[], env: RenderEnv, scope: RepeatScope): RenderedContent {
  const rendered: ReactNode[] = [];
  let hasContent = false;

  nodes.forEach((node, index) => {
    const child = renderNode(node, env, scope, `n${index}`);
    if (child === null || child === undefined) return;
    rendered.push(child);
    if (!isSeparator(node, env)) hasContent = true;
  });

  return { nodes: rendered, empty: nodes.length > 0 && !hasContent };
}

/** Render a child list, dropping nodes that resolved to nothing (decorations, which are never "empty"). */
export function renderChildren(nodes: TemplateNode[], env: RenderEnv, scope: RepeatScope): ReactNode[] {
  return renderContent(nodes, env, scope).nodes;
}

/** Exposed for the diff harness: does this node tree produce anything at all? */
export function pathValue(path: string, env: RenderEnv, scope: RepeatScope = {}): unknown {
  return resolvePath(path, env.ctx, scope);
}
